import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, BarChart3, Download, Loader2, MessageSquare } from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { useAnaliticasEncuesta, useExportarRespuestas } from '../hooks/useEncuestas'
import { useCampania } from '../hooks/useCampanias'
import { DispositivosChart } from '../components/encuestas/DispositivosChart'
import { CampoAnaliticas } from '../components/encuestas/CampoAnaliticas'

export function EncuestaAnaliticasPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaniaId = Number(id)

  const { setOverride, clearOverride } = useBreadcrumb()
  useDocumentTitle('Analíticas Encuesta')

  const { data: campania } = useCampania(campaniaId)
  const { data: analiticas, isLoading } = useAnaliticasEncuesta(campaniaId)
  const exportar = useExportarRespuestas()

  useEffect(() => {
    if (campania?.titulo) {
      setOverride(`/muestras-medicas/${id}`, campania.titulo)
    }
    return () => { clearOverride(`/muestras-medicas/${id}`) }
  }, [campania?.titulo, id, setOverride, clearOverride])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    )
  }

  if (!analiticas) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Sin datos de analíticas</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/muestras-medicas/${campaniaId}/encuestas`)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-teal-500" />
            Analíticas de Encuesta
          </h1>
        </div>
        <Button
          className="bg-teal-800 hover:bg-teal-900 text-white"
          onClick={() => exportar.mutate(campaniaId)}
          disabled={exportar.isPending}
        >
          {exportar.isPending
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <Download className="mr-2 h-4 w-4" />
          }
          Exportar Excel
        </Button>
      </div>

      {/* Total respuestas */}
      <Card
        className="max-w-xs border border-gray-300 dark:border-gray-700"
        style={{ backgroundImage: 'linear-gradient(to right, rgba(20,184,166,0.15), rgba(20,184,166,0.05) 50%, transparent)' }}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Total Respuestas</p>
              <p className="text-3xl font-bold text-black dark:text-white">{analiticas.total_respuestas}</p>
            </div>
            <MessageSquare className="h-5 w-5 text-teal-500" />
          </div>
        </CardContent>
      </Card>

      {/* Gráficas principales */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Respuestas por día */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Respuestas por Día</CardTitle>
          </CardHeader>
          <CardContent>
            {analiticas.respuestas_por_dia.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={analiticas.respuestas_por_dia}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="dia"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => new Date(v).toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(v) => new Date(v).toLocaleDateString('es-EC')}
                    formatter={(value: number) => [value, 'Respuestas']}
                  />
                  <Line type="monotone" dataKey="count" stroke="#14b8a6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* Dispositivos */}
        <DispositivosChart datos={analiticas.dispositivos} />
      </div>

      {/* Respuestas por campo */}
      {analiticas.respuestas_por_campo.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Distribución por Campo</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {analiticas.respuestas_por_campo.map((campo) => (
              <CampoAnaliticas
                key={campo.campo_id}
                etiqueta={campo.campo_etiqueta}
                tipo={campo.tipo}
                datos={campo.datos}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
